export type ShiftType = 'NIGHT' | 'DAY' | 'EVENING';

export const SHIFT_LABELS: Record<ShiftType, string> = {
  NIGHT: 'Night (12am – 8am)',
  DAY: 'Day (8am – 4pm)',
  EVENING: 'Evening (4pm – 12am)',
};

export const CUSTOMERS = ['Flex', 'HCE', 'NAN', 'Hiboo', 'NOCLAND'] as const;
export type Customer = (typeof CUSTOMERS)[number];

export const VALID_MAINTENANCE_CUSTOMERS = [...CUSTOMERS, 'Multi-Customer'] as const;
export type MaintenanceCustomer = (typeof VALID_MAINTENANCE_CUSTOMERS)[number];

export const ZNUNY_INSTANCES: Record<Customer, string> = {
  Flex: 'itsm-flex.nocland.com',
  HCE: 'itsm-hce.nocland.com',
  NAN: 'itsm-nan.nocland.com',
  Hiboo: 'itsm.hiboonetworks.com',
  NOCLAND: 'itsm.nocland.com',
};

export interface UrgentTicket {
  customer: Customer | string;
  ticketNumber: string;
  ticketSubject: string;
  whatNeedsToBeDone: string;
  znunyInstance: string;
  resolved: boolean;
  lastUpdatedBy?: string;
  assignedTo?: string;
}

export interface CurrentTicket {
  customer: Customer | string;
  ticketNumber: string;
  ticketSubject: string;
  actionSummary: string;
  nextSteps: string;
  znunyInstance: string;
  carriedForward: boolean;
  carriedForwardCount: number;
  firstAppearedDate: string;
  lastUpdatedBy?: string;
  assignedTo?: string;
}

export interface MaintenanceItem {
  customer: string;
  ticketNumber: string;
  description: string;
  status: string;
  scheduledDate: string | null;
  scheduledTime?: string | null;
  scheduledEndDate?: string | null;
  tppTicketNumber?: string;
}

export const CUSTOMER_COLORS: Record<string, string> = {
  Flex: '#ef4444',
  HCE: '#3b82f6',
  NAN: '#10b981',
  Hiboo: '#f59e0b',
  NOCLAND: '#8b5cf6',
  'Multi-Customer': '#ec4899',
};

export interface TrashedTicket {
  id: string;
  type: 'urgent' | 'current' | 'maintenance';
  ticket: UrgentTicket | CurrentTicket | MaintenanceItem;
  deletedAt: string;
  deletedFromShift: string;
  deletedBy: string;
}

export interface ShiftSummary {
  id?: string;
  date: string;
  shift: ShiftType;
  shiftStart: string;
  shiftEnd?: string;
  onDutyTech: string;
  onDutyTechId: string;
  submittedBy?: string;
  lastUpdatedBy?: string;
  contributors?: string[];
  submittedAt?: string;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  urgentTickets: UrgentTicket[];
  currentTickets: CurrentTicket[];
  maintenanceItems: MaintenanceItem[];
  notes: string;
}

/** Compute the previous shift for carry-forward queries */
export function getPreviousShift(date: string, shift: ShiftType): { date: string; shift: ShiftType } {
  if (shift === 'NIGHT') {
    // NIGHT (00:00-08:00) follows previous day's EVENING
    const d = new Date(date + 'T00:00');
    d.setDate(d.getDate() - 1);
    return { date: getLocalDateString(d), shift: 'EVENING' };
  }
  if (shift === 'DAY') {
    return { date, shift: 'NIGHT' };
  }
  // EVENING follows same-day DAY
  return { date, shift: 'DAY' };
}

/** Generate deterministic ID for a shift record */
export function getShiftRecordId(date: string, shift: ShiftType): string {
  return `${date}-${shift}`;
}

/** Detect current shift from the hour */
export function detectShift(date: Date = new Date()): ShiftType {
  const hour = date.getHours();
  if (hour >= 0 && hour < 8) return 'NIGHT';
  if (hour >= 8 && hour < 16) return 'DAY';
  return 'EVENING';
}

/** Format a Date as YYYY-MM-DD in local time. Must pair with detectShift() which also uses local time. */
export function getLocalDateString(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Get shift start time for a given date and shift */
export function getShiftStart(date: string, shift: ShiftType): string {
  const hours = shift === 'NIGHT' ? '00:00' : shift === 'DAY' ? '08:00' : '16:00';
  return `${date}T${hours}:00.000Z`;
}
